import { useState, type ReactNode } from 'react';
import { trackToolAction } from '../utils/analytics';

/** An event means the file arrived and a browser download was initiated, not that it was saved. */
export async function downloadFile(href: string, tool: string, action: string, format: string): Promise<boolean> {
  let objectUrl: string | undefined;
  let anchor: HTMLAnchorElement | undefined;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const source = new URL(href, window.location.origin);
    if (source.origin !== window.location.origin || source.username || source.password) return false;
    const response = await fetch(source.href, { credentials: 'omit', redirect: 'error', signal: controller.signal });
    if (!response.ok || response.headers.get('content-type')?.includes('text/html')) return false;
    const blob = await response.blob();
    if (!blob.size) return false;
    objectUrl = URL.createObjectURL(blob);
    anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = source.pathname.split('/').pop() || 'color-match-download';
    document.body.appendChild(anchor);
    anchor.click();
    trackToolAction(tool, action, format);
    return true;
  } catch { return false; }
  finally {
    clearTimeout(timeout);
    anchor?.remove();
    if (objectUrl) {
      const completedUrl = objectUrl;
      setTimeout(() => URL.revokeObjectURL(completedUrl), 1000);
    }
  }
}

export default function DownloadLink({ href, tool, action, format, className, children }: {
  href: string; tool: string; action: string; format: string; className?: string; children: ReactNode;
}) {
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState('');
  const [failed, setFailed] = useState(false);
  return <span>
    <a href={href} download className={className} aria-busy={pending} onClick={async event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (pending) return;
      setPending(true); setFailed(false); setStatus('Preparing download…');
      const ok = await downloadFile(href, tool, action, format);
      setPending(false); setFailed(!ok);
      setStatus(ok ? 'File received; browser download started. Check your downloads for the saved file.' : 'The file could not be fetched. Try the direct download link.');
    }}>{children}</a>
    {status && <span role="status" className="block mt-2 text-xs text-ink-secondary">{status}</span>}
    {failed && <a href={href} download className="inline-block mt-2 text-sm text-brand-700 underline">Direct download</a>}
  </span>;
}
